// Copyright 2024 Sorama B.V.

import { EventAggregator } from "aurelia-event-aggregator";
import { autoinject, bindable, BindingEngine, bindingMode, Disposable } from "aurelia-framework";
import { IToastError, Toast } from "controls/toast/Toast";
import { GenericMeasurement, Point3D, PsychoAcousticsResults, PsychoAcousticsValues } from "datatypes/SoramaWebApi";
import { ObjectExtensions } from "extensions/ObjectExtensions";
import { MeasurementColorService } from "services/Color/MeasurementColorService";
import { IMeasurementProcessorResolver } from "services/Measurement/IMeasurementProcessorResolver";
import { IPsychoAcousticsProcessingService } from "services/SoundProcessing/IPsychoAcousticsProcessingService";
import { BarkBand } from "units/BarkBand";
import { CriticalBand } from "units/CriticalBand";
import { FluctuationStrength, Loudness, Roughness, Sharpness, Tonality } from "units/PsychoAcousticsUnits";
import { CriticalBandUnit, Unit } from "units/Unit";
import { v4 as uuidv4 } from "uuid";
import { SpiderChartSVGManager } from "./Managers/SpiderChartSVGManager";
import { SpiderChartZoomManager } from "./Managers/SpiderChartZoomManager";

/**
 * Type representing the key values of the `PsychoAcousticMetricType` enum.
 * The values are the display name strings of the metrics.
 * Used for mapping between `PsychoAcousticMetricType` and other values, e.g. `PsychoAcousticAnalysisResult`.
 */
export type PsychoAcousticMetricType = "Loudness" | "Sharpness" | "Roughness" | "Fluctuation Strength" | "Tonality";

export const AllPsychoAcousticMetricTypes: PsychoAcousticMetricType[] = [
    "Loudness",
    "Sharpness",
    "Roughness",
    "Fluctuation Strength",
    "Tonality",
];

/**
 * Psychoacoustic units for each metric type.
 */
export const PsychoAcousticUnits: Record<PsychoAcousticMetricType, Unit> = {
    Loudness: Loudness.Sones,
    Sharpness: Sharpness.Acum,
    "Fluctuation Strength": FluctuationStrength.Vacils,
    Tonality: Tonality.tuHMS,
    Roughness: Roughness.Aspers,
};

/**
 * Units for the (frequency) band axis of each metric type
 */
export const PsychoAcousticBandUnits: Record<PsychoAcousticMetricType, CriticalBandUnit> = {
    Loudness: BarkBand.averaged,
    Sharpness: BarkBand.averaged,
    "Fluctuation Strength": CriticalBand.averaged,
    Tonality: CriticalBand.averaged,
    Roughness: CriticalBand.averaged,
};

/**
 * Mapper object to map `PsychoAcousticMetricType` keys to corresponding properties in `PsychoAcousticsValues`.
 * For example, maps `Loudness` metric to its respective field in `PsychoAcousticsValues`.
 */
export const Mapper: Record<PsychoAcousticMetricType, keyof PsychoAcousticsValues> = {
    Loudness: "Loudness",
    Sharpness: "Sharpness",
    "Fluctuation Strength": "Fluctuation",
    Tonality: "Tonality",
    Roughness: "Roughness",
};

export const METRIC_RANGES_MEASUREMENT: MetricRanges = {
    Loudness: { min: 1.0, max: 30.0 },
    Sharpness: { min: 0.5, max: 4.0 },
    Roughness: { min: 0.0, max: 1.0 },
    "Fluctuation Strength": { min: 0.0, max: 1.0 },
    Tonality: { min: 0.25, max: 3.0 },
};

export const METRIC_ORDER_CLOCKWISE_FROM_TOP: PsychoAcousticMetricType[] = [
    "Loudness",
    "Roughness",
    "Tonality",
    "Fluctuation Strength",
    "Sharpness",
];

interface MinMaxCalculationParams {
    values: Partial<PsychoAcousticAnalysisResult>[];
    metric: PsychoAcousticMetricType;
    fixedMax: number;
}

export type PsychoAcousticAnalysisResult = Record<PsychoAcousticMetricType, number>;

export type PsychoAcousticAnalysisResultPerMeasurement = {
    [measurementId: string]: PsychoAcousticAnalysisResult;
};

export type MetricRanges = Record<PsychoAcousticMetricType, { min: number; max: number }>;

/*
 * The SpiderChart class is responsible for orchestrating chart updates and delegating tasks to managers.
 */
@autoinject
export class SpiderChart {
    @bindable
    public measurements: GenericMeasurement[];

    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public selectedMic: Point3D;

    @bindable
    public spiderChartId: string;

    @bindable
    public selectedMeasurement: GenericMeasurement;

    @bindable
    public onBack;

    public svgManager: SpiderChartSVGManager;
    public zoomManager: SpiderChartZoomManager;

    private svgPlacerId = uuidv4();
    private toastId = uuidv4();

    private static readonly MAX_PROGRESS = 100;
    private colorObserverDisposables: Disposable[] = [];

    // Preserves the cancelation state of the previous updateData call
    private updateDataAbortController: AbortController;

    // Add a map to track request-specific progress
    private progressTracker: Map<string, number[]> = new Map();
    private currentRequestId: string;

    // Loading spinner
    public busy = false;
    public progress = 0;
    private subProgress = [];

    // Collection of the analysis results, containing microphone properties, and psychoacoustic values.
    public computedResults: PsychoAcousticAnalysisResultPerMeasurement = {};

    constructor(
        private readonly bindingEngine: BindingEngine,
        private readonly eventAggregator: EventAggregator,
        private readonly measurementColorService: MeasurementColorService,
        private readonly processorResolver: IMeasurementProcessorResolver
    ) {
        this.zoomManager = new SpiderChartZoomManager(this);
    }

    public attached() {
        this.svgManager = new SpiderChartSVGManager(this.svgPlacerId);
        this.eventAggregator.subscribe("clearSVG-" + this.spiderChartId, () => {
            this.svgManager.close();
            this.abortOngoingRequest();
        });
        this.measurementsChanged();
    }

    public detached() {
        while (this.colorObserverDisposables.length > 0) {
            this.colorObserverDisposables.pop()?.dispose();
        }
        this.abortOngoingRequest();
        this.svgManager.close();
        this.computedResults = {};
        this.progressTracker.clear();
    }

    public changeMicrophone() {
        this.svgManager.close();
        this.onBack();
    }

    private abortOngoingRequest(): void {
        if (this.updateDataAbortController) {
            this.updateDataAbortController.abort();
        }
    }

    public computeProgress() {
        if (this.subProgress.length == 0) {
            this.progress = SpiderChart.MAX_PROGRESS;
            return;
        }

        // Calculate average progress: sum / length
        this.progress =
            (SpiderChart.MAX_PROGRESS * this.subProgress.reduce((a, b) => a + b, 0)) / this.subProgress.length;
    }

    /**
     * Set the progress of a specific measurement
     */
    public setSubProgress(idx: number, progress: number, requestId: string) {
        // Ignore progress updates from old requests
        if (requestId !== this.currentRequestId) return;

        const currentProgress = this.progressTracker.get(requestId);
        if (currentProgress) {
            currentProgress[idx] = progress;
            this.subProgress = [...currentProgress];
            this.computeProgress();
        }
    }

    //#region Measurement Management

    /**
     * Handles changes in the selected measurement and updates the highlight.
     * @param newValue The new selected measurement.
     * @param oldValue The previously selected measurement.
     */
    public selectedMeasurementChanged(newValue: GenericMeasurement, oldValue: GenericMeasurement) {
        if (oldValue) {
            this.clearHighlightMeasurement(oldValue);
        }
        this.highlightMeasurement(this.selectedMeasurement);
    }

    /**
     * Change the selection of a measurement.
     * Toggle the selection if the measurement is already selected.
     * @param measurement
     */
    public selectMeasurement(measurement: GenericMeasurement) {
        this.selectedMeasurement = this.selectedMeasurement === measurement ? undefined : measurement;
    }

    /**
     * Highlights a measurement in the spider chart by displaying the created polygon on top
     * @param measurement The measurement to highlight
     */
    public highlightMeasurement(measurement: GenericMeasurement) {
        if (!measurement) return;

        const color = this.measurementColorService.getColor({ measurementId: measurement.id })?.color.hex;
        const values = this.computedResults[measurement.id];
        this.svgManager.highlightMeasurement(measurement, color, values);
    }

    /**
     * Clears the highlight of a measurement in the spider chart. Unless the measurement is selected.
     */
    public clearHighlightMeasurement(measurement: GenericMeasurement) {
        if (measurement === this.selectedMeasurement) return;

        this.svgManager.clearHighlightMeasurement(measurement);
        this.highlightMeasurement(this.selectedMeasurement);
    }

    /**
     * Handles changes in measurements and updates the spider chart accordingly.
     */
    public async measurementsChanged() {
        if (!(this.svgManager && this.svgManager.hasSpiderChartBeenCreated())) return;

        if (this.shouldSkipChartUpdate()) return;

        this.clearInvalidMeasurementSelection();

        this.zoomManager.resetZooming();
        await this.updateChartWithErrorHandling();
    }

    /**
     * Determines if the spider chart update should be skipped based on measurement changes.
     * @returns True if the update can be skipped; otherwise, false.
     */
    public shouldSkipChartUpdate(): boolean {
        const measurementIds = this.measurements.map((m) => m.id);
        const measurementIdsInChart = this.svgManager.getAllMeasurementIdsInSpiderChart();

        return (
            measurementIds.length === measurementIdsInChart.length &&
            measurementIdsInChart.every((id) => measurementIds.includes(id))
        );
    }

    /**
     * Clears the selected measurement if it is no longer present in the list of measurements.
     */
    public clearInvalidMeasurementSelection(): void {
        if (this.selectedMeasurement && !this.measurements.find((m) => m.id === this.selectedMeasurement.id)) {
            this.selectedMeasurement = undefined;
        }
    }

    /**
     * Get the psychoacoustic results of a measurement
     * @param measurementIdx The index of the measurement in the measurements array
     * @returns The returned psychoacoustic object from the backend
     */
    public async getMeasurementPsychoAcoustics(
        measurementIdx: number,
        signal: AbortSignal
    ): Promise<Partial<PsychoAcousticAnalysisResult>> {
        const { id: measurementId, name } = this.measurements[measurementIdx];
        const requestId = this.currentRequestId; // Capture current request ID

        try {
            const soundProcessingService =
                await this.processorResolver.resolveProcessor<IPsychoAcousticsProcessingService>(
                    IPsychoAcousticsProcessingService,
                    this.measurements[measurementIdx]
                );
            if (signal.aborted) return;

            // Send request to retrieve psychoacoustic analysis results for the measurement
            const result: PsychoAcousticsResults = await soundProcessingService.getPsychoAcoustics(
                this.measurements[measurementIdx],
                { requestedMicrophone: this.selectedMic },
                (progress: number) => this.setSubProgress(measurementIdx, progress, requestId),
                signal // Pass the abort signal to the request so that it can be aborted
            );
            if (signal.aborted) return;

            if (!result?.data) {
                throw new Error("Invalid response: missing data field");
            }

            this.computedResults[measurementId] = {} as PsychoAcousticAnalysisResult;
            for (const metricType of AllPsychoAcousticMetricTypes) {
                this.computedResults[measurementId][metricType] = result.data[Mapper[metricType]];
            }

            this.setSubProgress(measurementIdx, 1, requestId);
            return this.computedResults[measurementId];
        } catch (error) {
            console.error(error);
            throw {
                title: `Error processing measurement '${name}'`,
                message: "Please select a different measurement or contact Sorama.",
                name: "AnalysisError",
            } as IToastError;
        }
    }

    /**
     * Adds a measurement to the spider chart. This method removes any existing measurement with the same ID,
     * ensures the measurement has an assigned color, and adds it to the chart with the given scaled values.
     * @param measurement - The measurement to be added (GenericMeasurement).
     * @param scaledValues - The corresponding scaled values (PsychoAcousticAnalysisResult).
     * @returns A promise that resolves when the measurement is added.
     */
    public async addMeasurementToSpiderChart(
        measurement: GenericMeasurement,
        scaledValues: PsychoAcousticAnalysisResult
    ) {
        // If the measurement is already in the spider chart, remove it first
        this.svgManager.removeMeasurementFromSpiderChart(measurement.id);

        // Assign measurement color if it doesn't exist
        this.measurementColorService.ensureAssignedColor({ measurementId: measurement.id });

        this.colorObserverDisposables.push(
            this.measurementColorService.subscribe({ measurementId: measurement.id }, () => {
                this.colorMeasurementChanged(measurement.id);
            })
        );

        const color = this.measurementColorService.getColor({ measurementId: measurement.id })?.color.hex;

        const hasStroke =
            Object.keys(this.zoomManager.hasPolygonStroke).length === 0
                ? true
                : this.zoomManager.hasPolygonStroke[measurement.id];

        const polygon = this.svgManager.addMeasurementToSpiderChart(measurement, scaledValues, color, hasStroke);
        this.addEventsToMeasurementPolygon(polygon, measurement);
    }

    /**
     * Updates the color of the polygon representing the measurement.
     * Extracts the color from the measurementColorService
     * @param measurementId The ID of the measurement
     * @param elementId The ID of the SVG element representing the measurement
     */
    public colorMeasurementChanged(measurementId: string) {
        const color = this.measurementColorService.getColor({ measurementId: measurementId }).color.hex;

        this.svgManager.changeMeasurementColor(measurementId, color);
    }

    //#endregion

    //#region Scaling

    /**
     * Updates the spider chart and handles errors gracefully.
     */
    private async updateChartWithErrorHandling(): Promise<void> {
        try {
            await this.updateSpiderChart();
        } catch (error) {
            console.error(error);
            Toast.displayError(error, this.toastId);
        } finally {
            this.busy = false;
        }
    }

    /**
     * Update the spider chart with the latest measurement values
     */
    private async updateSpiderChart() {
        // Reset loading spinner and UI state
        this.progress = 0;
        this.busy = true;

        // Cancel any ongoing updateData request
        this.abortOngoingRequest();

        // Create new request ID and controller
        this.currentRequestId = uuidv4();

        // Create a new controller for this request
        this.updateDataAbortController = new AbortController();
        const signal = this.updateDataAbortController.signal;

        // Initialize progress tracker for this request
        this.progressTracker.set(this.currentRequestId, Array(this.measurements.length).fill(0));

        // Remove all measurements that are not in the new list
        this.svgManager.clearAllMeasurementsFromSpiderChart();
        this.subProgress = Array(this.measurements.length).fill(0);
        if (signal.aborted) return;

        const measurementPromises = this.measurements.map((m, idx) => {
            // Check if the measurement has already been computed
            if (!this.computedResults[m.id]) {
                return this.getMeasurementPsychoAcoustics(idx, signal);
            } else {
                if (signal.aborted) return;
                this.setSubProgress(idx, 1, this.currentRequestId);
                return Promise.resolve(this.computedResults[m.id]);
            }
        });

        // Wait for all measurements to be computed and collect results
        const results = await Promise.all(measurementPromises);
        if (signal.aborted) return;

        this.onPsychoAcousticResultsReady();

        // Scale and plot the values
        this.normalizeAndPlotValues(results);
        this.busy = false;
    }

    /**
     * Normalizes and plots the values for the given results.
     * If only one result is provided, it adjusts the scaling based on fixed limits for a single measurement.
     * If multiple results are provided, it dynamically scales the data using an auto-scaled range.
     * @param result - Array of PsychoAcousticsResults containing the data to be plotted.
     */
    private normalizeAndPlotValues(result: Partial<PsychoAcousticAnalysisResult>[]) {
        if (result.length == 0) return;

        const ranges = this.getDynamicRanges(result);
        this.zoomManager.currentRanges = ranges;

        result.forEach((res, i) => {
            // Assert that res contains all required properties
            const normalizedResult = res as PsychoAcousticAnalysisResult;
            const values = this.normalizeValues(normalizedResult, ranges);
            this.addMeasurementToSpiderChart(this.measurements[i], values);
        });
    }

    /**
     * Determines the ranges for normalization, handling single and multiple measurements.
     * @param results - Array of PsychoAcousticsResults.
     * @returns MetricRanges for scaling.
     */
    private getDynamicRanges(results: Partial<PsychoAcousticAnalysisResult>[]): MetricRanges {
        return results.length === 1
            ? this.adjustSingleMeasurementRange(results[0])
            : this.calculateMinMaxRangeMultipleMeasurements(results);
    }

    /**
     * Adjusts the metric ranges for a single measurement by using fixed ranges.
     * If the values fall outside the fixed min or max, it adjusts the range accordingly.
     * @param values - PsychoAcousticAnalysisResult for the single measurement to be plotted.
     * @returns Adjusted MetricRanges that accommodate the given values.
     */
    private adjustSingleMeasurementRange(values: Partial<PsychoAcousticAnalysisResult>): MetricRanges {
        const newRanges: MetricRanges = ObjectExtensions.copy(METRIC_RANGES_MEASUREMENT);

        // To ensure that the values for a single measurement don’t exceed the fixed limits but can be adjusted if they do
        for (const metricType of AllPsychoAcousticMetricTypes) {
            const originalRange = METRIC_RANGES_MEASUREMENT[metricType];
            const value = values[metricType];
            newRanges[metricType] = this.adjustRangeWithOffset(
                Math.min(originalRange.min, value),
                Math.max(originalRange.max, value),
                METRIC_RANGES_MEASUREMENT[metricType].max
            );
        }

        return newRanges;
    }

    /**
     * Calculates the minimum and maximum range for multiple measurements.
     * This function evaluates each metric across all measurements and determines dynamic min and max values.
     * @param values - Array of PsychoAcousticAnalysisResult for each measurement.
     * @returns MetricRanges with dynamically adjusted min and max values for each metric.
     */
    private calculateMinMaxRangeMultipleMeasurements(values: Partial<PsychoAcousticAnalysisResult>[]): MetricRanges {
        const ranges: MetricRanges = AllPsychoAcousticMetricTypes.reduce((acc, metric) => {
            if (values.length === 0) {
                acc[metric] = METRIC_RANGES_MEASUREMENT[metric];
            } else {
                const fixedMax = METRIC_RANGES_MEASUREMENT[metric]?.max;
                acc[metric] = this.calculateMinMax({ values, metric, fixedMax });
            }
            return acc;
        }, {} as MetricRanges);

        // Adjust ranges if values are too close
        Object.keys(ranges).forEach((key) => {
            const { min, max } = ranges[key];
            ranges[key] = this.adjustRangeWithOffset(min, max, METRIC_RANGES_MEASUREMENT[key].max, 0.7, 0.8);
        });

        return ranges;
    }

    /**
     * Adjusts the range with offset based on a fixed maximum value and tolerance.
     * @param min - The calculated minimum value.
     * @param max - The calculated maximum value.
     * @param fixedMax - The fixed maximum value to compare against.
     * @param minMaxTolerance - The tolerance percentage for adjusting the range (default: 30%).
     * @param offsetPercentage - The percentage of fixedMax to offset min and max (default: 5%).
     * @returns Adjusted min and max values.
     */
    private adjustRangeWithOffset(
        min: number,
        max: number,
        fixedMax: number,
        minMaxTolerance = 0.05, // tolerance for min-max being too close (5% by default)
        offsetPercentage = 0.01 // offset percentage to adjust the range by (1% by default)
    ): { min: number; max: number } {
        if (max - min < minMaxTolerance * fixedMax) {
            return {
                min: Math.max(0, min - offsetPercentage * fixedMax),
                max: Math.min(fixedMax, max + offsetPercentage * fixedMax),
            };
        }
        // Return original range if not too close
        return { min, max };
    }

    /**
     * Calculates the minimum and maximum values for a given metric key.
     * Dynamically calculate min/max values across multiple measurements and
     * adjusts the range when the values are too close to each other.
     * @param values - Array of PsychoAcousticAnalysisResult for each measurement.
     * @param key - The key of the metric to calculate min and max for.
     * @param fixedMax - The fixed maximum value to compare against.
     * @returns An object with the min and max values for the given metric.
     */
    private calculateMinMax(params: MinMaxCalculationParams): { min: number; max: number } {
        const { values, metric, fixedMax } = params;
        const metricValues = values.map((v) => v[metric]).filter((x) => x !== undefined);
        if (metricValues.length === 0) {
            return METRIC_RANGES_MEASUREMENT[metric];
        }
        // Gets the minimum and maximum from all the selected measurements
        const min = Math.min(...metricValues);
        const max = Math.max(...metricValues);
        return this.adjustRangeWithOffset(min, max, fixedMax);
    }

    /**
     * Scales the values for each metric based on the given range.
     * The resulting values are normalized between 0 and 1.
     * @param values - PsychoAcousticAnalysisResult to be scaled.
     * @param ranges - MetricRanges containing the min and max for each metric.
     * @returns Scaled PsychoAcousticAnalysisResult normalized between 0 and 1.
     */
    public normalizeValues(values: PsychoAcousticAnalysisResult, ranges: MetricRanges): PsychoAcousticAnalysisResult {
        const normalizedValues: PsychoAcousticAnalysisResult = {} as PsychoAcousticAnalysisResult;
        for (const metric in ranges) {
            normalizedValues[metric] = this.normalizeValue(values[metric], ranges[metric]);
        }
        return normalizedValues;
    }

    /**
     * Normalizes a given value between 0 and 1 based on the provided range and introduces
     * a small offset to avoid hitting the exact extremes of 0 and 1. Takes into account
     * overshoot and undershoot adjustments for better scaling.
     * @param value - The value to be scaled.
     * @param range - An object containing the min and max values for scaling.
     * @returns The normalized value between 0 and 1, avoiding extreme values.
     */
    private normalizeValue(value: number, range: { min: number; max: number }): number {
        if (range.min === range.max) {
            return 0.5; // Handle case where min and max are equal
        }

        // Ensure that if the value is zero to skip scaling it
        if (value == 0) return 0;

        // Normalize to [0, 1]
        const scaledValue = (value - range.min) / (range.max - range.min);

        const lowerOffset = 0.0;
        const upperOffset = 0.01; // Allows the values to reach closer to 1

        const normalizedValue = scaledValue * (1 - lowerOffset - upperOffset) + lowerOffset;

        // Ensure values are bounded between [0, 1]
        return Math.max(0, Math.min(1, normalizedValue));
    }

    //#endregion

    //#region Event Handling

    /**
     * Add events that enable the measurement to be selected and highlighted
     * @param polygon The SVG element representing the measurement
     * @param measurement The measurement that the polygon represents
     */
    private addEventsToMeasurementPolygon(polygon: SVGElement, measurement: GenericMeasurement) {
        polygon.onclick = () => {
            this.selectMeasurement(measurement);
        };
        polygon.onmouseover = () => {
            this.highlightMeasurement(measurement);
            // Highlight the selected measurement in the measurement list in the html based on id;
            const element = document.getElementById(this.svgManager.getMeasurementDivElementId(measurement.id));
            element?.classList.add("hover-effect");
        };
        polygon.onmouseout = () => {
            this.clearHighlightMeasurement(measurement);
            this.highlightMeasurement(this.selectedMeasurement); // Ensures the selected measurement stays on top
            const element = document.getElementById(this.svgManager.getMeasurementDivElementId(measurement.id));
            element?.classList.remove("hover-effect");
        };

        // Add zoom event handling for mouse wheel scrolling
        this.svgManager.svg.onwheel = (event: WheelEvent) => {
            // Detect scroll direction (zoom in or out)
            let zoomDirection = event.deltaY < 0 ? "zoomIn" : "zoomOut";

            // Trigger zoom on the hovered metric
            this.zoomManager.handleZoomOnMetric(zoomDirection);
        };

        // Indicate that the polygon is clickable
        polygon.style.cursor = "pointer";
    }

    /**
     * Dispatch the computed psychoacoustic results once they are retrieved.
     */
    public onPsychoAcousticResultsReady() {
        this.eventAggregator.publish("spider-chart:psycho-acoustic-results-ready", this.computedResults);
    }

    /**
     * Handles the mouse over event for a measurement.
     */
    public onMouseOver(measurement: GenericMeasurement): void {
        this.highlightMeasurement(measurement);
    }

    /**
     * Handles the mouse out event for a measurement.
     */
    public onMouseOut(measurement: GenericMeasurement): void {
        this.clearHighlightMeasurement(measurement);
    }

    //#endregion
}
